/**
 * tsEstimator.ts
 * ─────────────────────────────────────────────────────────────────────────
 * Estimate Thiele/Small parameters from the legacy Subwoofer schema fields
 * (size, sealedVol, portedVol, portedTuning, xmax, rms) when verified
 * manufacturer T/S specs are not yet populated.
 *
 * Estimates flagged with `estimated: true` so the UI can warn the user that
 * the prediction is approximate. Once a sub gets verified specs, it falls
 * back to those automatically.
 *
 * Estimation method:
 *   1. Fs from driver size + Xmax (longer-throw drivers tend to have lower Fs)
 *   2. Qts from driver class (high-power SPL subs tend to Qts 0.45–0.55,
 *      SQ subs 0.30–0.40) — guessed from the recommended sealed volume:
 *      tight sealed boxes imply low Qts, loose sealed boxes imply high Qts.
 *   3. Vas back-calculated from the recommended sealed volume targeting
 *      a Qtc of 0.707 (most manufacturers spec the "flat" sealed volume).
 *   4. Re from impedance string (single-coil rated impedance × 0.85).
 *   5. Sd from cutout diameter (cone diameter ≈ cutout − 0.75").
 *   6. Qes/Qms split: Qes = Qts × 1.2, Qms = (Qts × Qes) / (Qes − Qts).
 */

import type { ThieleSmall } from "./boxPhysics";
import { ft3ToLiters } from "./boxPhysics";

interface LegacySubFields {
  size: number;          // inches nominal
  sealedVol: number;     // ft³ per sub (manufacturer recommended sealed)
  portedVol: number;     // ft³ per sub (manufacturer recommended ported)
  portedTuning: number;  // Hz (recommended port tuning)
  xmax: number;          // mm one-way
  rms: number;           // watts
  cutoutDia: number;     // inches
  impedance: string;     // e.g. "Dual 2ohm", "Single 4ohm"
}

/** Industry-average free-air resonance for modern car-audio drivers, Hz. */
const FS_BASE_BY_SIZE: Record<number, number> = {
  6: 45,
  8: 38,
  10: 33,
  12: 28,
  15: 24,
  18: 21,
  21: 18,
};

const fsForSize = (sizeIn: number): number => {
  if (FS_BASE_BY_SIZE[sizeIn]) return FS_BASE_BY_SIZE[sizeIn];
  const sizes = Object.keys(FS_BASE_BY_SIZE).map(Number).sort((a, b) => a - b);
  if (sizeIn <= sizes[0]) return FS_BASE_BY_SIZE[sizes[0]];
  if (sizeIn >= sizes[sizes.length - 1]) return FS_BASE_BY_SIZE[sizes[sizes.length - 1]];
  for (let i = 0; i < sizes.length - 1; i++) {
    if (sizeIn >= sizes[i] && sizeIn <= sizes[i + 1]) {
      const t = (sizeIn - sizes[i]) / (sizes[i + 1] - sizes[i]);
      return FS_BASE_BY_SIZE[sizes[i]] * (1 - t) + FS_BASE_BY_SIZE[sizes[i + 1]] * t;
    }
  }
  return 30;
};

/** Parse an impedance string like "Dual 2ohm" → effective single-coil Ω. */
function parseImpedanceOhms(impedance: string): number {
  const match = impedance.match(/(\d+(?:\.\d+)?)\s*ohm/i);
  if (!match) return 4;
  const ohms = parseFloat(match[1]);
  return ohms;
}

/** Effective cone area (cm²) from cutout diameter (Sd ≈ cone disc area). */
function sdFromCutout(cutoutDiaIn: number): number {
  // Effective cone diameter is ~85% of cutout (cutout > cone surround)
  const coneDiaIn = cutoutDiaIn * 0.85;
  const coneDiaCm = coneDiaIn * 2.54;
  return Math.PI * Math.pow(coneDiaCm / 2, 2);
}

export function estimateThieleSmall(s: LegacySubFields): ThieleSmall {
  // 1. Fs — base from size, lowered if Xmax is high (long-throw → softer suspension)
  const fsBase = fsForSize(s.size);
  const xmaxNorm = Math.max(0, Math.min(1, (s.xmax - 8) / 25)); // 0 at 8mm, 1 at 33mm+
  const fsHz = Math.max(15, fsBase * (1 - xmaxNorm * 0.18));

  // 2. Qts — guess from how tight the recommended sealed box is (relative to size).
  // Smaller sealed vol per inch² of cone → driver wants tight loading → lower Qts
  // Larger sealed vol → loose driver → higher Qts (more electrical damping needed)
  const sealedPerInch3 = s.sealedVol / Math.pow(s.size, 1.4); // empirical normalisation
  let qts: number;
  if (sealedPerInch3 < 0.0035) qts = 0.36;       // tight SQ-leaning
  else if (sealedPerInch3 < 0.0050) qts = 0.42;  // balanced
  else if (sealedPerInch3 < 0.0075) qts = 0.48;  // SPL-leaning
  else qts = 0.55;                               // loose SPL/comp

  // 3. Vas — back-calc from manufacturer's recommended sealed volume targeting Qtc 0.707
  // Qtc = Qts · √(1 + Vas/Vb)  →  Vas = Vb · ((Qtc/Qts)² − 1)
  const qtcTarget = 0.707;
  const vbRecommendedL = ft3ToLiters(s.sealedVol);
  const vasFactor = Math.pow(qtcTarget / qts, 2) - 1;
  let vasL = Math.max(vbRecommendedL * vasFactor, vbRecommendedL * 0.5);

  // Sanity clamp: Vas should be 0.5×–4× recommended sealed vol for typical car subs
  vasL = Math.max(vbRecommendedL * 0.5, Math.min(vbRecommendedL * 4, vasL));

  // 4. Re from impedance (DC resistance ≈ 0.85 × nominal Z)
  const reOhms = parseImpedanceOhms(s.impedance) * 0.85;

  // 5. Sd from cutout
  const sdCm2 = sdFromCutout(s.cutoutDia);

  // 6. Qes/Qms split. Qts = (Qes·Qms)/(Qes+Qms). Most car subs run Qes ≈ Qts × 1.15
  const qes = qts * 1.15;
  // Solve for Qms: 1/Qts = 1/Qes + 1/Qms  →  Qms = (Qts·Qes)/(Qes − Qts)
  const qms = (qts * qes) / Math.max(qes - qts, 0.01);

  return {
    fsHz: Math.round(fsHz * 10) / 10,
    vasL: Math.round(vasL * 10) / 10,
    qts: Math.round(qts * 1000) / 1000,
    qes: Math.round(qes * 1000) / 1000,
    qms: Math.round(qms * 100) / 100,
    reOhms: Math.round(reOhms * 100) / 100,
    sdCm2: Math.round(sdCm2 * 10) / 10,
    xmaxMm: s.xmax,
    rmsW: s.rms,
    estimated: true,
  };
}
